import { useWorkforceStats } from "@/hooks/workforce/useWorkforceStats";
import { useWorkforceBench } from "@/hooks/workforce/useWorkforceBench";
import { useWorkforceFilter } from "@/contexts/WorkforceFilterContext";
import { Card } from "@/components/ui/card";
import { Users, AlertTriangle, Clock, Pause, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export const WorkforceStats = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { stats, isLoading } = useWorkforceStats();
	const { stats: benchStats, isLoading: benchLoading } = useWorkforceBench();
	const { setStatusFilter, setEndingSoon, endingSoon, statusFilter } = useWorkforceFilter();

	const handleStatClick = (type: 'active' | 'onHold' | 'endingSoon' | 'endingThisWeek' | 'bench') => {
		if (type === 'bench') {
			navigate('/workforce/bench');
			return;
		}

		// Clear other filters first
		setEndingSoon(null);

		if (type === 'active') {
			setStatusFilter(['active']);
		} else if (type === 'onHold') {
			setStatusFilter(['on_hold']);
		} else if (type === 'endingSoon') {
			setStatusFilter(['active']);
			setEndingSoon(30);
		} else if (type === 'endingThisWeek') {
			setStatusFilter(['active']);
			setEndingSoon(7);
		}
	};

	if (isLoading || benchLoading) {
		return (
			<div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
				{[1, 2, 3, 4, 5].map(i => (
					<Skeleton key={i} className="h-20 rounded-sm" />
				))}
			</div>
		);
	}

	const statCards = [
		{
			id: 'active',
			label: t("workforce.stats.active", "Active"),
			value: stats.active,
			icon: Users,
			isSelected: statusFilter.length === 1 && statusFilter[0] === 'active' && !endingSoon,
			accent: stats.active > 0 ? "text-emerald-600 dark:text-emerald-400" : undefined
		},
		{
			id: 'onHold',
			label: t("workforce.stats.onHold", "On Hold"),
			value: stats.onHold,
			icon: Pause,
			isSelected: statusFilter.length === 1 && statusFilter[0] === 'on_hold',
			accent: stats.onHold > 0 ? "text-amber-600 dark:text-amber-400" : undefined
		},
		{
			id: 'bench',
			label: t("workforce.stats.bench", "On Bench"),
			sublabel: benchStats.totalDailyBenchCost > 0
				? `${benchStats.currency} ${benchStats.totalDailyBenchCost}/day`
				: undefined,
			value: benchStats.totalOnBench,
			icon: UserX,
			isSelected: false,
			accent: benchStats.totalOnBench > 0 ? "text-orange-600 dark:text-orange-400" : undefined
		},
		{
			id: 'endingSoon',
			label: t("workforce.stats.endingSoon", "Ending Soon"),
			sublabel: t("workforce.stats.within30Days", "within 30 days"),
			value: stats.endingSoon,
			icon: Clock,
			isSelected: endingSoon === 30 ,
			accent: stats.endingSoon > 0 ? "text-amber-600 dark:text-amber-400" : undefined
		},
		{
			id: 'endingThisWeek',
			label: t("workforce.stats.endingThisWeek", "Urgent"),
			sublabel: t("workforce.stats.within7Days", "within 7 days"),
			value: stats.endingThisWeek,
			icon: AlertTriangle,
			isSelected: endingSoon === 7 ,
			accent: stats.endingThisWeek > 0 ? "text-rose-600 dark:text-rose-400" : undefined
		}
	];

	return (
		<div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
			{statCards.map(card => (
				<Card
					key={card.id}
					className={cn(
						"p-4 cursor-pointer transition-all hover:shadow-md",
						card.isSelected && "ring-2 ring-primary ring-offset-2"
					)}
					onClick={() => handleStatClick(card.id as any)}
				>
					<div className="flex items-start justify-between">
						<div className="flex flex-col">
							<span className={cn("text-2xl font-bold", card.accent)}>{card.value}</span>
							<span className="text-sm font-medium text-foreground">
								{card.label}
							</span>
							{card.sublabel && (
								<span className="text-xs text-muted-foreground">{card.sublabel}</span>
							)}
						</div>
						<div className="p-2 rounded-sm bg-muted">
							<card.icon className={cn("h-5 w-5", card.accent || "text-muted-foreground")} />
						</div>
					</div>
				</Card>
			))}
		</div>
	);
};
